/**
 * Chat page - conversation view with header, messages, and input.
 * Uses background1.png pattern and design from reference.
 */

import backgroundPattern from "@assets/images/background1.png";
import { ChatBubble } from "@features/messaging/components/ChatBubble";
import { RealtimePausedBanner } from "@features/messaging/components/RealtimePausedBanner";
import { useChatMessages } from "@features/messaging/hooks/useChatMessages";
import type { ChatPageOtherUser } from "@features/messaging/hooks/useChatPage";
import { useChatPage } from "@features/messaging/hooks/useChatPage";
import type { Message } from "@features/messaging/stores/messagingStore";
import { useLayoutContext } from "@router/layouts/MainLayout";
import clsx from "clsx";
import { formatDistanceToNowStrict } from "date-fns";
import { SendHorizontal } from "lucide-react";
import { useEffect, useState } from "react";
import type { NavigateFunction } from "react-router-dom";

function timeAgo(date: Date): string {
  if (Date.now() - date.getTime() < 60_000) {
    return "just now";
  }
  return formatDistanceToNowStrict(date, { addSuffix: true });
}

function getReceiptLabel(lastMessage: Message | undefined, currentUserId: string): string | false {
  if (!lastMessage || lastMessage.sender_uid !== currentUserId) {
    return false;
  }
  const readAt = lastMessage.read_timestamp;
  if (readAt != null) {
    return `Read ${timeAgo(readAt)}`;
  }
  const sentAt = lastMessage.timestamp;
  if (sentAt != null) {
    return `Sent ${timeAgo(sentAt)}`;
  }
  return false;
}

function ChatPageContent({
  currentUserId,
  chatId,
  otherUser,
  navigate,
}: {
  currentUserId: string;
  chatId: string;
  otherUser: ChatPageOtherUser | undefined;
  navigate: NavigateFunction;
}) {
  const { messages, sendMessage, error: messagesError, retry: retryMessages } = useChatMessages(chatId, currentUserId);
  const [inputValue, setInputValue] = useState("");
  const [realtimeBannerDismissed, setRealtimeBannerDismissed] = useState(false);
  const displayName = otherUser?.display_name ?? "Unknown";
  const lastMessage = messages[messages.length - 1];
  const receiptLabel = getReceiptLabel(lastMessage, currentUserId);
  const hasInput = inputValue.trim() !== "";

  useEffect(() => {
    if (!messagesError) {
      setRealtimeBannerDismissed(false);
    }
  }, [messagesError]);

  const handleSend = () => {
    const text = inputValue.trim();
    if (text) {
      sendMessage(text);
      setInputValue("");
    }
  };

  const { setHeader } = useLayoutContext();

  useEffect(() => {
    const goToProfile = otherUser?.uid != null ? () => navigate(`/match/${otherUser.uid}`) : undefined;

    setHeader({
      variant: "back",
      onBack: () => navigate(-1),
      centerContent: goToProfile ? (
        <button
          type="button"
          onClick={goToProfile}
          className="flex min-w-0 flex-1 items-center gap-3 text-left hover:opacity-90"
          aria-label={`View ${displayName}'s profile`}
          data-testid="chat-header-profile-link"
        >
          <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-300">
            {otherUser?.photo_url ? (
              <img src={otherUser.photo_url} alt="" className="h-full w-full object-cover" />
            ) : (
              <span className="text-sm font-medium text-gray-500">{displayName.charAt(0).toUpperCase()}</span>
            )}
          </div>
          <span className="truncate font-mono font-semibold text-xl text-gray-900" data-testid="chat-other-user-name">
            {displayName}
          </span>
        </button>
      ) : (
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-300">
            {otherUser?.photo_url ? (
              <img src={otherUser.photo_url} alt="" className="h-full w-full object-cover" />
            ) : (
              <span className="text-sm font-medium text-gray-500">{displayName.charAt(0).toUpperCase()}</span>
            )}
          </div>
          <span className="truncate font-mono font-semibold text-xl text-gray-900" data-testid="chat-other-user-name">
            {displayName}
          </span>
        </div>
      ),
      menuOptions:
        otherUser?.uid != null ? [{ label: "View profile", onClick: () => navigate(`/match/${otherUser.uid}`) }] : [],
    });
    return () => setHeader(null);
  }, [setHeader, navigate, otherUser?.uid, otherUser?.photo_url, displayName]);

  // No messages yet and subscription failed — nothing to keep on screen
  if (messagesError && messages.length === 0) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50 p-8" data-testid="chat-page">
        <div className="text-center">
          <h2 className="text-xl font-semibold font-mono text-gray-900 mb-2">Live updates paused</h2>
          <p className="text-gray-600 mb-6">Could not load messages.</p>
          <button
            type="button"
            onClick={retryMessages}
            className="bg-lavender-400 text-white px-6 py-3 rounded-lg font-semibold hover:bg-lavender-500 transition-colors"
            data-testid="chat-retry-button"
          >
            Reconnect
          </button>
        </div>
      </div>
    );
  }

  const showRealtimeBanner = Boolean(messagesError) && messages.length > 0 && !realtimeBannerDismissed;

  return (
    <div
      className="flex flex-col h-full min-h-0 overflow-hidden"
      data-testid="chat-page"
      style={{
        backgroundColor: "var(--color-lavender-950)",
        backgroundImage: `url(${backgroundPattern})`,
        backgroundRepeat: "repeat",
        backgroundSize: "auto",
      }}
    >
      {showRealtimeBanner ? (
        <RealtimePausedBanner
          testIdPrefix="chat"
          onReconnect={retryMessages}
          onDismiss={() => setRealtimeBannerDismissed(true)}
        />
      ) : null}
      {/* Messages area */}
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col px-4 py-4">
        {messages.length === 0 ? (
          <div className="flex flex-1 flex-col justify-center">
            <p className="text-center text-gray-400 text-sm" data-testid="chat-empty-state">
              No messages yet. Say hi!
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {messages.map((msg) => (
              <ChatBubble key={msg.id} message={msg} isSent={msg.sender_uid === currentUserId} />
            ))}
            {receiptLabel ? (
              <p className="self-end font-mono text-white text-sm font-bold" data-testid="chat-read-receipt">
                {receiptLabel}
              </p>
            ) : null}
          </div>
        )}
      </div>

      {/* Input area */}
      <div className="shrink-0 p-4">
        <div className="flex items-center gap-2 rounded-full bg-[#17072d]/80 border-3 border-lavender-500 px-4 py-2 ">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            className="flex-1 bg-transparent border-none outline-none text-white placeholder-gray-500 py-1"
            data-testid="chat-message-input"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
          <button
            type="button"
            onClick={handleSend}
            className={clsx("p-2 rounded-full  hover:bg-gray-200 text-gray-600")}
            aria-label="Send"
            data-testid="chat-send-button"
          >
            <SendHorizontal
              className={clsx("w-7 h-7  stroke-1", {
                "bg-[#17072d]/80": !hasInput,
                "stroke-gray-500/50": !hasInput,
                "fill-secondary": hasInput,
                "stroke-white/50": hasInput,
              })}
            />
          </button>
        </div>
      </div>
    </div>
  );
}

export function ChatPage() {
  const { currentUserId, chatId, otherUser, chatNotFound, navigate } = useChatPage();

  if (chatNotFound) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Chat not found</h2>
          <p className="text-gray-600">Missing chat ID.</p>
          <button
            type="button"
            onClick={() => navigate("/matches")}
            className="mt-4 text-lavender-600 hover:underline"
            data-testid="back-to-matches"
          >
            Back to Matches
          </button>
        </div>
      </div>
    );
  }

  if (!currentUserId || !chatId) {
    return null;
  }

  return <ChatPageContent currentUserId={currentUserId} chatId={chatId} otherUser={otherUser} navigate={navigate} />;
}
